import React, { CSSProperties, memo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Icon } from '@workboard/action-item'
import styled from 'styled-components';
import { Draggable } from 'react-beautiful-dnd';
import Card from './card';
import { AiTypeSelect } from '../aiType';
import { IAICardProps } from '../../types/ai.types';
import * as CardAction from '../../actions/card.actions';
import * as SidePanelAction from '../../actions/sidePanel.actions';
import ImgIconComp from '../../../components/imgcomp';
import { getAiTypeAttributes, getStateComponentIcon, getAiTypeDefinitions } from '../../utils';

const AICardBottomWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    justify-content: space-between;
`

const AiCardName = styled.div<{
    backgroundColor: string;
}>`
    &::after {
        background: ${({ backgroundColor }) => backgroundColor};
    }
`;

interface IMenuIconProps {
    count: any,
    iconClass: string
}

const MenuIcon = (props: IMenuIconProps) => {
    return (
        <div className='count-item'>
            <div className='count-label'>
                {props.count}
            </div>
            <div className={`count-icon icon-${props.iconClass}`}></div>
        </div>
    )
}

const AiStateIconWrapper = styled.div`
 svg {
    padding-top: 2px;
}
`;
const AiStateIcon = ({ name, width = '18', height = '18' }) => Icon({
    name,
    width,
    height
})


const getItemStyle = (isDragging: boolean, draggableStyle: any) => {
    const { transform, ...rest } = draggableStyle;
    let style = {
        // some basic styles to make the items look a bit nicer
        userSelect: "none",
        // styles we need to apply on draggables
        ...draggableStyle,
    }
    if (isDragging && transform) {
        let containerCoordinates = document.querySelector(".mainBoardWrapper").getBoundingClientRect();

        let trArray = transform.substring(10, transform.length - 1).split(",");
        let transX = draggableStyle.left + parseInt(trArray[0].trim().substring(0, trArray[0].length - 2));
        let transY = draggableStyle.top + parseInt(trArray[1].trim().substring(0, trArray[1].length - 2));

        if (transY < containerCoordinates.top - 10) {
            if (transX > containerCoordinates.left) {
                style.transform = `translate(${transX - draggableStyle.left}px, -${draggableStyle.top - (containerCoordinates.top - 10)}px)`;
            } else {
                style.transform = `translate(-${draggableStyle.left - containerCoordinates.left}px, -${draggableStyle.top - (containerCoordinates.top - 10)}px)`;
            }

        } else if (transX < containerCoordinates.left) {
            style.transform = `translate(-${draggableStyle.left - containerCoordinates.left}px, ${transY - draggableStyle.top}px)`;
        } else {
            style.transform = transform;
        }
    }
    return style;
};

const AICard = ({
    columnId,
    card,
    highlightedAI,
    memberHighlightedId,
    user,
    wsPermissions,
    isActive,
    index,
    showIssueTypeIcon,
    highlightIssuesByType,
}: IAICardProps) => {
    const { t } = useTranslation();
    const { is_co_manager, is_manager, is_team_admin } = wsPermissions || {}
    const isAdminPermission: boolean = (is_co_manager || is_manager || is_team_admin);
    const firstName = card.owner_firstname || '';
    const lastName = card.owner_lastname || '';
    const { doneCount = 0, totalCount = 0 } = card.subCount || {};
    const subCount: string = totalCount > 0 ? `${doneCount > 0 ? doneCount : 0}/${totalCount > 0 ? totalCount : 0}` : '';
    const isSubaction: boolean = Boolean(card.parent);
    const showPriorityIcon: boolean = card.priority == 1 || card.priority == 2;
    let cardStyle: CSSProperties = {};
    let dueDate: string = t('No Date');

    if (card.dueDateText) {
        dueDate = card.dueDateText;
    }
    if (card.rag == 1) {
        cardStyle.borderLeft = '4px solid #EAA241';
        cardStyle.width = 'calc(100% - 15px)';
        cardStyle.overflow = 'hidden';
    }
    else if (card.rag == 2) {
        cardStyle.borderLeft = '4px solid #DA3D26';
        cardStyle.width = 'calc(100% - 15px)';
    }

    const cardType = Number(card.type);

    if (highlightIssuesByType) {
        const typeDefinition = getAiTypeDefinitions().find(type => type.id === cardType);
        if (typeDefinition?.colors?.cardBackground) {
            cardStyle.backgroundColor = typeDefinition.colors.cardBackground;
        }
    }

    if (highlightedAI && user && card.owner == user.id) {
        cardStyle.backgroundColor = '#FFF8E1';
    }
    if (memberHighlightedId && card.owner == memberHighlightedId) {
        cardStyle.backgroundColor = '#E1F2F1';
    }
    if (isActive) {
        cardStyle.backgroundColor = '#80CBC4';
    }


    const { backgroundColor = 'white' } = cardStyle;
    const dispatch = useDispatch();
    let elementClickCount1 = 0;
    let elementTimer1: number = null;
    const cardId = card.id;
    const isNewCard = cardId.indexOf('newAi_') !== -1;
    const dueDateVal: number = (card.dueDateText && card.dueDateText != 'No Date') ? new Date(card.dueDateText).getTime() / 1000 : 0;
    let dueDateStyle: any = {};
    const now = new Date(new Date().setHours(0, 0, 0, 0)).getTime() / 1000;
    if (
        typeof dueDateVal !== "undefined" &&
        dueDateVal != 0 &&
        dueDateVal < now && card.state != 0
    ) {
        dueDateStyle.color = '#c1272d';
    }

    const { attributes } = getAiTypeAttributes(card.type);

    const aiOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, aiItem: any) => {
        if (isNewCard) return;
        elementClickCount1 += 1;
        if (elementClickCount1 === 1 && aiItem.type == 0) {
            if (cardId) {
                elementTimer1 = window.setTimeout(() => {
                    elementClickCount1 = 0;
                    dispatch(CardAction.aiCardClick({ columnId, ...aiItem }));
                }, 200);
            }
        } else {
            clearTimeout(elementTimer1);
            elementClickCount1 = 0;
            const aiId = cardId;
            const obj = "wsBoardAiFullDet";
            const urlVal = `${wbConfig.baseUrl}/activity/aI?id=${aiId}`;
            if (urlVal && aiId && obj) {
                dispatch(SidePanelAction.closeSidePanel());
                return openFullPopUp(aiFullViewPopUp, urlVal, $(obj));
            }
        }
    }

    const isDraggableDisabled = (id: string) => {
        if (id && (id.includes("newAi_") || (!isAdminPermission && user.id != card.owner))) {
            return true;
        }
        return false;
    }

    const viewFullDetails = (e) => {
        e.stopPropagation();
        e.preventDefault();
        let urlVal = `${wbConfig.baseUrl}/activity/aI?id=${card.id}`;
        openFullPopUp(aiFullViewPopUp, urlVal, $(e.target));
    };

    let fullDetailsUrl = `/wb/actionitem?id=${card.id}`;

    return (
        <div onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => { aiOnClick(e, card) }}>
            <Draggable draggableId={`drag-item-${card.id}`} index={index} isDragDisabled={isDraggableDisabled(card.id)} >
                {
                    (provided: any, snapshot: any) => (
                        <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                            style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                            )}
                        >
                            <Card
                                style={cardStyle}
                                highlightIssuesByType={highlightIssuesByType}
                            >
                                <Card.Body>
                                    <div className='ai-card-title' data-index-count={index}>
                                        {isSubaction && <span role="img" aria-label={t('subaction item')}>↪ &nbsp;</span>}
                                        <AiCardName
                                            backgroundColor={backgroundColor}
                                            className='ai-card-name'
                                            dangerouslySetInnerHTML={{ __html: card.description }}
                                        />
                                        {
                                            showPriorityIcon && (
                                                <div style={{ width: '16px' }}>
                                                    <AiStateIcon
                                                        width="15"
                                                        height="15"
                                                        name={getStateComponentIcon(attributes?.priority?.values ?? [], card.priority)}
                                                    />
                                                </div>
                                            )
                                        }
                                        <a onClick={viewFullDetails}
                                            href={fullDetailsUrl}
                                            title={t('View Full Details of Action Item')}
                                            className='ai-card-full-detail-link'
                                            aria-hidden='true'
                                        >
                                            <ImgIconComp id="full_det_img" width="14" height="14" alt={t('Action Item Full Details')} />
                                        </a>
                                    </div>
                                    <div className='ai-card-subtitle'>
                                        <div className='ai-card-owner truncateText'>
                                            {`${firstName} ${lastName}`}
                                        </div>
                                        <div className='ai-card-date'>
                                            {isSubaction && (
                                                <ImgIconComp
                                                    class="wsBoardSubActImg"
                                                    id="subact_img"
                                                    width="12"
                                                    height="8"
                                                />
                                            )}
                                            <span style={dueDateStyle}>{dueDate}</span>
                                        </div>
                                        <AiStateIconWrapper>
                                            <AiStateIcon
                                                name={getStateComponentIcon(attributes?.state?.values ?? [], card.state)}
                                            />
                                        </AiStateIconWrapper>
                                    </div>
                                    <AICardBottomWrapper className='ai-card-bottom' data-testid="ai-card-bottom">
                                        <div className='aicard-bottom-column'>
                                            {Number(card.commentCount) ? <MenuIcon count={card.commentCount} iconClass='comment' data-testid="comment" /> : ''}
                                            {subCount ? <MenuIcon count={subCount} iconClass='subcount' /> : ''}
                                            {Number(card.attachCount) ? <MenuIcon count={card.attachCount} iconClass='attch' /> : ''}
                                            {Number(card.loopCount) ? <MenuIcon count={card.loopCount} iconClass='loop' /> : ''}
                                            {Number(card.tagCount) ? <MenuIcon count={card.tagCount} iconClass='tagcount' /> : ''}
                                            {Number(card.relationshipsCount) ? <MenuIcon count={card.relationshipsCount} iconClass='relationshipscount' /> : ''}
                                        </div>
                                        {showIssueTypeIcon && !isNaN(Number(card.type)) ?
                                            <div className='aicard-bottom-column'>
                                                <AiTypeSelect ai={card} />
                                            </div>
                                            : ''
                                        }
                                    </AICardBottomWrapper>
                                </Card.Body>
                            </Card>
                        </div>
                    )
                }

            </Draggable>
        </div>
    )
};

export default memo(AICard);
